import { type Action, isAnyOf, type ListenerEffectAPI } from '@reduxjs/toolkit';
import { isDefined, isEmpty, isNotDefined } from '@sgme/fp';

import { getWorkspaceMatchFulfilled } from '@/api/workspace/index.ts';
import { espApi } from '@/store/esp/api';
import { espStreamSubscribed, espStreamUnsubscribed } from '@/store/esp/slice.ts';
import { getEspStreamId } from '@/store/esp/utils.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import type { AppDispatch, AppState } from '@/store/store.ts';
import { addAppListener, type AppListenerEffect } from '@/store/storeMethods.ts';
import { targetAccumulatorSlice } from '@/store/targetAccumulator/slice.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { addTile, duplicateTile, removeTile, updateTileProductType } from '@/store/tiles/slice.ts';
import { getTodayDateOnly } from '@/utils/date.ts';

async function registerListener(tileId: string, api: ListenerEffectAPI<AppState, AppDispatch>) {
  const effectsAfterTileChanged = [tieringEffect(tileId), espSubscriptionEffect(tileId)];

  const unsubscribe = api.dispatch(
    addAppListener({
      predicate: (action, currentState, previousState) => {
        return (
          !targetAccumulatorSlice.actions.targetAccumulatorClientIdUpdated.match(action) &&
          isTargetAccumulatorTileChanged(tileId, previousState, currentState)
        );
      },

      effect: (action, api) => {
        for (const effect of effectsAfterTileChanged) {
          effect(action, api);
        }
      },
    }),
  );

  const isTargetAccumulator = api.getOriginalState().tiles.entities[tileId]?.productType === 'TargetAccumulator';

  if (await api.condition(unsubscribeCondition(tileId, isTargetAccumulator))) {
    unsubscribe();
  }
}

export const registerEspTargetAccumulatorListeners = (startAppListening: StartAppListening) => {
  startAppListening({
    actionCreator: addTile,

    effect: async (action, api) => {
      const {
        tile: { tileId },
      } = action.payload;

      await registerListener(tileId, api);
    },
  });
  startAppListening({
    actionCreator: updateTileProductType,

    effect: async (action, api) => {
      const { tileId } = action.payload;

      await registerListener(tileId, api);
    },
  });
  startAppListening({
    actionCreator: duplicateTile,

    effect: async (action, api) => {
      const { newTileId } = action.payload;

      await registerListener(newTileId, api);
    },
  });
  startAppListening({
    matcher: getWorkspaceMatchFulfilled,

    effect: async (_action, api) => {
      const currentState = api.getState();
      for (const product of targetAccumulatorSlice.selectors.getAllTargetAccumulatorProducts(currentState)) {
        const { tileId } = product;
        await registerListener(tileId, api);
      }
    },
  });

  startAppListening({
    actionCreator: targetAccumulatorSlice.actions.targetAccumulatorClientIdUpdated,
    effect: async (action, api) => {
      const { tileId } = action.payload;
      const previousTile = api.getOriginalState().targetAccumulator[tileId];
      if (action.payload.isTabClientOverriden && previousTile?.clientId === action.payload.clientId) {
        return;
      }

      espUnsubscriptionEffect(tileId)(action, api);
      await tieringEffect(tileId)(action, api);
    },
  });

  startAppListening({
    predicate: isAnyOf(removeTile, updateTileProductType),
    effect: async (action, api) => {
      const tileId = removeTile.match(action)
        ? action.payload
        : updateTileProductType.match(action)
          ? action.payload.tileId
          : undefined;

      if (isDefined(tileId) && api.getOriginalState().tiles.entities[tileId]?.productType === 'TargetAccumulator') {
        espUnsubscriptionEffect(tileId)(action, api);
      }
    },
  });
};

function unsubscribeCondition(tileId: string, isTargetAccumulator: boolean) {
  return (action: Action) => {
    return (
      (removeTile.match(action) && action.payload === tileId) ||
      (isTargetAccumulator && updateTileProductType.match(action) && action.payload.tileId === tileId)
    );
  };
}

const tieringEffect =
  (tileId: string): AppListenerEffect =>
  async (_action, api) => {
    const state = api.getState();
    const currentTile = state.targetAccumulator[tileId];
    if (isNotDefined(currentTile)) {
      return;
    }

    // has already a tiering ?
    if (isDefined(currentTile.properties.tiering)) {
      return;
    }

    // all required parameters are not ready ?
    if (isEmpty(currentTile.properties.currencyPair)) {
      return;
    }

    const result = await api.dispatch(
      espApi.endpoints.getEspTiering.initiate({
        clientId: selectTileClientId(state, tileId),
        currencyPair: currentTile.properties.currencyPair,
        spotDate: getTodayDateOnly(),
      }),
    );

    if ('data' in result && isDefined(result.data) && !('error' in result.data)) {
      api.dispatch(
        targetAccumulatorSlice.actions.targetAccumulatorTieringUpdated({ tileId, tiering: result.data.tiering }),
      );
    }
  };

const espSubscriptionEffect =
  (tileId: string): AppListenerEffect =>
  (_action, api) => {
    const currentState = api.getState();

    const currentTile = currentState.targetAccumulator[tileId];

    if (isNotDefined(currentTile)) {
      return;
    }

    // all required parameters are not ready ?
    if (isEmpty(currentTile.properties.currencyPair) || isEmpty(currentTile.properties.tiering)) {
      return;
    }

    const espId = getEspStreamId(currentTile.properties.currencyPair, currentTile.properties.tiering);
    const espStream = currentState.esp.streams[espId];

    // has already subscribed ?
    if (isDefined(espStream) && espStream.subscribedTileIds.includes(tileId)) {
      return;
    }

    api.dispatch(
      espStreamSubscribed({
        tileId,
        currencyPair: currentTile.properties.currencyPair,
        tiering: currentTile.properties.tiering,
        bdrId: selectTileClientId(currentState, tileId),
      }),
    );
  };

const espUnsubscriptionEffect =
  (tileId: string): AppListenerEffect =>
  (_action, api) => {
    const currentState = api.getState();

    const previousTile = api.getOriginalState().targetAccumulator[tileId];
    const currentTile = currentState.targetAccumulator[tileId];
    if (isNotDefined(previousTile)) {
      return;
    }

    // all required parameters for ESP not present ?
    if (isEmpty(previousTile.properties.currencyPair) || isEmpty(previousTile.properties.tiering)) {
      return;
    }

    const espId = getEspStreamId(previousTile.properties.currencyPair, previousTile.properties.tiering);
    const espStream = currentState.esp.streams[espId];

    // not previously subscribed ?
    if (isNotDefined(espStream) || !espStream.subscribedTileIds.includes(tileId)) {
      return;
    }

    // current tile is still compatible with ESP ?
    if (
      isDefined(currentTile) &&
      currentTile.properties.currencyPair === previousTile.properties.currencyPair &&
      currentTile.properties.tiering === previousTile.properties.tiering
    ) {
      return;
    }

    api.dispatch(
      espStreamUnsubscribed({
        tileId,
        currencyPair: previousTile.properties.currencyPair,
        tiering: previousTile.properties.tiering,
      }),
    );
  };

const isTargetAccumulatorTileChanged = (tileId: string, previousState: AppState, currentState: AppState) => {
  const currentTile = currentState.targetAccumulator[tileId];
  const previousTile = previousState.targetAccumulator[tileId];
  return currentTile !== previousTile;
};
